import type { PropsWithChildren, ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { UseMultiselectFilterParams } from "./hooks/useMultiselectFilter";
import { useMultiselectFilter } from "./hooks/useMultiselectFilter";
import type { OptionValue } from "./types";
import { Chip } from "./ui/Chip";

export type SimpleMultiselectFilterProps<K = string> = {
    dataAutomation: string;
    isFilterSelected: boolean;
    options: OptionValue<K>[];
    renderOption?: (item: OptionValue<K>, isSelected: boolean) => ReactNode;
} & Pick<UseMultiselectFilterParams<K>, "appliedItems" | "onApplyChanges">;

const defaultRenderOption: SimpleMultiselectFilterProps["renderOption"] = (item, isSelected) => (
    <Flex
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
        width="100%"
    >
        <Combobox.OptionLabel>{item.label}</Combobox.OptionLabel>
        <Combobox.OptionCheckbox isChecked={isSelected} />
    </Flex>
);

export const SimpleMultiselectFilter = <K extends string>({
    appliedItems,
    children,
    dataAutomation,
    isFilterSelected,
    onApplyChanges,
    options,
    renderOption = defaultRenderOption,
}: PropsWithChildren<SimpleMultiselectFilterProps<K>>) => {
    const { translate } = useTranslate();

    const { isOpen, onOpenChange, onReset, onItemSelect, onApply, tempSelectedItems } = useMultiselectFilter<K>({
        onApplyChanges,
        appliedItems,
    });

    return (
        <Combobox
            isOpen={isOpen}
            onOpenChange={onOpenChange}
        >
            <Combobox.Trigger>
                <Chip
                    isSelected={isFilterSelected}
                    onReset={onReset}
                    data-automation={dataAutomation}
                >
                    {children}
                </Chip>
            </Combobox.Trigger>

            <Combobox.Content>
                {options.map((item) => (
                    <Combobox.Option
                        key={item.id}
                        value={item}
                        onClick={() => onItemSelect(item)}
                        textValue={item.id}
                    >
                        {renderOption(item, tempSelectedItems.has(item.id))}
                    </Combobox.Option>
                ))}
            </Combobox.Content>
            <Combobox.ApplyButton onClick={onApply}>{translate("common.apply")}</Combobox.ApplyButton>
        </Combobox>
    );
};
